import { Button, Flex, Heading3, InputText, Span } from "@goodfill/ui-components";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FieldGroup } from "../../../form/FieldGroup";
import { route } from "../../../routes";
import { zRequiredEmail } from "../../../form/zod/zodTypes";
import { ForgotPasswordLockIcon } from "./ForgotPasswordLockIcon";
import { zodErrorsToString } from "../../../form/zod/zodErrorsToString";
import { forgotPassword } from "@goodfill/backend-api-client";
import { toastErrorHandler } from "../../../unexpectedErrorHandler";
import { backendFetcher } from "../../backendApi/backendApi";
import { useLogout } from "../../useLogout";

export interface ForgotPasswordProps {
  onSubmit: (username: string) => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onSubmit }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [wasEmailFiledTouched, setWasEmailFieldTouched] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const logout = useLogout();

  const handleForgetPassword = async () => {
    setIsLoading(true);
    (await forgotPassword(backendFetcher, { username: email })).match({
      success: () => {
        onSubmit(email);
      },
      businessMatcher: {
        limitExceeded: toastErrorHandler,
        userNotFound: toastErrorHandler,
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });
    setIsLoading(false);
  };

  const schema = zRequiredEmail;
  const validation = schema.safeParse(email);
  const submitDisabled = !validation.success;

  return (
    <Flex direction="col" gap={8} items={"center"}>
      <ForgotPasswordLockIcon />

      <Flex direction="col" gap={0} width={"w-full"}>
        <Flex direction="col" gap={2}>
          <Heading3>Forgot Password?</Heading3>
          <Flex>Enter your email and we'll send you instructions to reset your password.</Flex>
        </Flex>

        <FieldGroup
          label={"Email Address"}
          validation={wasEmailFiledTouched ? zodErrorsToString(validation) : []}
          fill
        >
          <InputText
            id="email"
            type="email"
            disabled={isLoading}
            value={email}
            setValue={setEmail}
            border={!wasEmailFiledTouched || validation.success ? "neutral" : "red"}
            onBlur={() => setWasEmailFieldTouched(true)}
          />
        </FieldGroup>

        <Flex verticalMargin={4}>
          <Button
            fill
            data-testid="signInButton"
            id="signInButton"
            primary={true}
            loading={isLoading}
            disabled={submitDisabled}
            onClick={handleForgetPassword}
          >
            Send Email Code
          </Button>
        </Flex>

        <Flex direction="row" gap={0} justify={"center"} className="md:mt-20 mt-9">
          <Span
            data-testid="signUpLink"
            id="signUpLink"
            primary={true}
            size="sm"
            selectNone={true}
            onClick={() => navigate(route.signIn())}
          >
            Back to login
          </Span>
        </Flex>
      </Flex>
    </Flex>
  );
};
